import { combineLatest, Observable } from 'rxjs'
import { filter, map, switchMap, take, tap } from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { Component, OnInit } from '@angular/core'
import { RequestQueryBuilder } from '@nestjsx/crud-request'
import { Store } from '@ngrx/store'
import { BulkAction, BulkActionEvent } from 'src/app/shared/interfaces'
import { NotificationService } from 'src/app/core/notification.service'
import { Booking } from '../../booking/booking'
import { UntilDestroy } from '@ngneat/until-destroy'
import { bookingActions } from '../../booking/booking.actions'
import { bookingSelectors } from '../../booking/booking.selectors'
import { courseSelectors } from '../course.selectors'
import { Course } from '../course'
import { courseActions } from '../course.actions'
import { AccessRights } from '../../role/user-role/user-role'
import { TranslateService } from '@ngx-translate/core'
import { ActivatedRoute } from '@angular/router'
import { BsModalService } from 'ngx-bootstrap/modal'
import { DeleteBookingsModalComponent } from '../../booking/delete-bookings-modal/delete-bookings-modal.component'

@UntilDestroy()
@Component({
  selector: 'course-bookings-create-tab',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class CourseBookingCreateTabComponent implements OnInit {
  public accessRights = AccessRights
  public course$: Observable<Course>
  public courseId$: Observable<number>
  public isLoading$: Observable<boolean>
  public isLoadingBooking$: Observable<boolean>
  public bookings$: Observable<Booking[]>

  public BulkAction = BulkAction
  private didFetchCourse = false

  constructor(
    private store: Store<AppState>,
    private translate: TranslateService,
    private notificationService: NotificationService,
    private activeRoute: ActivatedRoute,
    private modalService: BsModalService
  ) {}

  ngOnInit() {
    this.isLoadingBooking$ = this.store.select(bookingSelectors.selectIsLoading)

    this.courseId$ = this.activeRoute.params.pipe(
      map((params) =>
        params.hasOwnProperty('courseId') ? Number(params['courseId']) : null
      ),
      filter((id) => Boolean(id)),
      map((id) => +id),
      tap((courseId) => {
        if (!this.didFetchCourse) {
          this.store.dispatch(
            courseActions.fetchOneById({
              id: courseId,
              params: RequestQueryBuilder.create({
                join: [{ field: 'product' }, { field: 'product.translations' }]
              })
            })
          )

          this.store.dispatch(
            bookingActions.fetchMany({
              params: RequestQueryBuilder.create({
                search: { courseId },
                join: [
                  { field: 'tags' },
                  { field: 'product' },
                  { field: 'product.translations' },
                  { field: 'product.location' },
                  { field: 'linkedProduct' },
                  { field: 'linkedProduct.translations' },
                  { field: 'supervisorBookings' }
                ]
              })
            })
          )
          this.didFetchCourse = true
        }
      })
    )

    this.course$ = this.courseId$.pipe(
      switchMap((courseId) =>
        this.store
          .select(courseSelectors.selectEntities)
          .pipe(map((entities) => entities[courseId]))
      )
    )

    this.bookings$ = combineLatest([
      this.store.select(bookingSelectors.selectAll),
      this.course$
    ]).pipe(
      map(([bookings, course]) => {
        if (!bookings?.length || !course?.id) {
          return []
        }

        return bookings
          .filter((b) => b.courseId === course.id)
          .sort((a, b) => (a?.startDate > b?.startDate ? -1 : 1))
      })
    )
  }

  public onBulkAction(bulkActionEvent: BulkActionEvent, type: string) {
    this.course$.pipe(take(1)).subscribe((course) => {
      if (!course) {
        this.notificationService.emit('alerts.error', 'error')
        return
      }

      switch (bulkActionEvent.action) {
        case BulkAction.DELETE:
          const modalRef = this.modalService.show(
            DeleteBookingsModalComponent,
            {
              initialState: {
                bookingIds: bulkActionEvent.items.map((item) =>
                  parseInt(item.id)
                )
              }
            }
          )
          break
      }
    })
  }
}
